import * as React from "react";
import { EventId, paceMaxDefault, RaceId } from "../../../domain/event";
import {
    EventDetailRace,
    raceDistance,
    RaceSegment,
} from "../../../hooks/event";
import { Distance, ValidIsoDateString } from "../../../domain/common";
import {
    array,
    monoid,
    nonEmptyArray,
    number,
    option,
    ord,
    record,
    string,
    tuple,
} from "fp-ts";
import {
    ResultWithCategoryTitle,
    useResultsWithCategoryTitle,
} from "../../../hooks/results";
import { flow, pipe } from "fp-ts/function";
import {
    isCompletedState,
    queryStateData,
} from "../../../utils/supabase/QueryState";
import { emptyArray, pick, rightOrThrow, someOrThrow } from "../../../utils/fp";
import { useAlertOnQueryError } from "../../../hooks/useAlertOnQueryError";
import {
    GridColumns,
    GridEnrichedColDef,
} from "@mui/x-data-grid/models/colDef/gridColDef";
import {
    GridValueFormatterParams,
    GridValueGetterParams,
} from "@mui/x-data-grid/models/params/gridCellParams";
import {
    bibPassAfterStartView,
    StoredTimeEntry,
    storedTimeEntryCodec,
    timeEntryFromStored,
    timeEntryFromTimeStringCodec,
    timeEntryToString,
} from "../../../domain/timing";
import { DataTable } from "../../DataTable";
import { contramap, Ord } from "fp-ts/Ord";
import { Option } from "fp-ts/Option";
import * as t from "io-ts";
import { useQuery } from "../../../utils/supabase/useQuery";
import { column, columns } from "../../../utils/pg-ts/pg-ts";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { ClubScoring } from "../../../domain/race";
import { assertKey, Nullable } from "../../../utils/types";
import { raceNumberOrd } from "../../../domain/result";
import { concatAll } from "fp-ts/Monoid";
import { assertUnreachable } from "../../../utils/assertUnreachable";
export interface LiveResultsProps {
    eventId: EventId;
    race: EventDetailRace;
    gunshot: ValidIsoDateString;
    categoryFilter: Array<string>;
}

export const LiveResultsIndividuals = (p: LiveResultsProps) => {
    const individualsRows = useIndividualsRows(p);

    const tableCols = React.useMemo(() => {
        const cols: Array<Nullable<GridEnrichedColDef<TableRowIndividual>>> = [
            {
                field: "rank",
                headerName: "Pořadí",
                maxWidth: 50,
            },
            {
                field: "name",
                headerName: "Jméno",
                flex: 0.5,
                maxWidth: 200,
                minWidth: 150,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.name,
            },
            {
                field: "sex",
                headerName: "Pohlaví",
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.sex,
            },
            {
                field: "born",
                headerName: "Ročník",
                maxWidth: 75,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.born,
            },
            {
                field: "number",
                headerName: "SČ",
                maxWidth: 75,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.number,
                sortComparator: raceNumberOrd.compare,
            },
            {
                field: "club",
                headerName: "Tým",
                maxWidth: 200,
                flex: 0.5,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.club_title,
            },
            {
                field: "bib",
                headerName: "Čip",
            },
            {
                field: "category",
                flex: 0.5,
                sortable: false,
                headerName: "Kategorie",
                filterable: false,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowIndividual>
                ) => params.row.result.category_title,
            },
            pipe(p.race.kind, (kind) => {
                switch (kind) {
                    case "duration":
                        return {
                            field: "laps",
                            headerName: "Kol",
                            type: "number",
                            maxWidth: 75,
                        };

                    case "distance":
                        return null;
                }
                assertUnreachable(kind);
            }),
            {
                field: "checkpointSum",
                headerName: "Čas",
                valueFormatter: ({ value }) =>
                    pipe(
                        value,
                        option.fromNullable,
                        option.fold(
                            () => "",
                            flow(timeEntryFromStored, timeEntryToString)
                        )
                    ),
            },
            ...array.makeBy(
                individualsRows.checkpointsCount > 1
                    ? individualsRows.checkpointsCount
                    : 0,
                (i) => ({
                    field: `checkpoint_${i}`,
                    headerName: `${i + 1}. kolo`,
                    flex: 0.5,
                    maxWidth: 100,
                    minWidth: 100,
                    valueGetter: (
                        params: GridValueGetterParams<any, TableRowIndividual>
                    ) =>
                        params.row.checkpointTimes[i] -
                        pipe(
                            params.row.checkpointTimes[i - 1],
                            option.fromNullable,
                            option.getOrElse(() => 0)
                        ),
                    valueFormatter: (
                        params: GridValueFormatterParams<StoredTimeEntry>
                    ) =>
                        pipe(
                            params.value,
                            storedTimeEntryCodec.decode,
                            option.fromEither,
                            option.fold(
                                () => "",
                                flow(timeEntryFromStored, timeEntryToString)
                            )
                        ),
                })
            ),
        ];

        return pipe(cols, array.filterMap(option.fromNullable));
    }, [individualsRows.checkpointsCount, p.race.kind]);

    return (
        <DataTable
            loading={individualsRows.loading}
            columns={tableCols}
            rows={individualsRows.rows}
            initialState={{
                columns: {
                    columnVisibilityModel: {
                        bib: false,
                        born: false,
                        sex: false,
                    },
                },
            }}
        />
    );
};

interface BibPassWithGunshotRelativeTimeEntry extends BibPass {
    startRelativeTimeEntry: StoredTimeEntry;
}

interface Checkpoint {
    timeLimit: StoredTimeEntry;
    distance: Distance;
}

interface BibPassWithComputedIgnore
    extends BibPassWithGunshotRelativeTimeEntry {
    computedIgnore: NonNullable<BibPass["ignored"]>;
}

const ordByGunshotRelativeTimeEntry = contramap(
    (item: BibPassWithGunshotRelativeTimeEntry) => item.startRelativeTimeEntry
)(number.Ord);

interface TableRowIndividual {
    id: string;
    bib: string;
    result: ResultWithCategoryTitle;
    rank: number;
    checkpointTimes: Array<StoredTimeEntry>;
    laps: number;
    checkpointSum: StoredTimeEntry;
}
type TableRowIndividualWithoutRank = Omit<TableRowIndividual, "rank">;

const M = ord.getMonoid<TableRowIndividualWithoutRank>();
const ordByOrdLapsDesc = pipe(
    number.Ord,
    contramap((item: TableRowIndividualWithoutRank) => item.laps),
    ord.reverse
);
const ordByCheckpointSum = pipe(
    number.Ord,
    contramap((item: TableRowIndividualWithoutRank) => item.checkpointSum)
);
const lapsRaceOrd = concatAll(M)([ordByOrdLapsDesc, ordByCheckpointSum]);

const bibPassColumns = pipe(
    bibPassAfterStartView.columns,
    pick("time", "bib", "id", "ignored")
);
const bibPassCodec = t.type(bibPassColumns, "BibPass");
type BibPass = t.TypeOf<typeof bibPassCodec>;
const useBibPassAfterStart = (raceId: RaceId) => {
    return useQuery(
        `bib_pass_after_start${raceId}}`,
        (supabase) =>
            supabase
                .from(bibPassAfterStartView.name)
                .select(columns(bibPassColumns))
                .eq(column(bibPassAfterStartView, "race_id"), raceId),
        t.array(bibPassCodec),
        {
            refreshInterval: 2000,
        }
    );
};

interface RankAcc {
    rank: number;
    withSameClassification: number;
    prev: TableRowIndividualWithoutRank;
}

const addRank =
    (ord: Ord<TableRowIndividualWithoutRank>) =>
    (
        rows: NonEmptyArray<TableRowIndividualWithoutRank>
    ): Array<TableRowIndividual> => {
        const initAcc: RankAcc = {
            rank: 1,
            withSameClassification: 0,
            prev: nonEmptyArray.head(rows),
        };

        return pipe(
            rows,
            nonEmptyArray.sort(ord),
            array.scanLeft(initAcc, (acc, current) => {
                const isAfterPrevResult = !ord.equals(current, acc.prev);

                const rankDiff = isAfterPrevResult
                    ? acc.withSameClassification
                    : 0;

                const withSameClassification = isAfterPrevResult
                    ? 1
                    : acc.withSameClassification + 1;

                return {
                    rank: acc.rank + rankDiff,
                    withSameClassification,
                    prev: current,
                };
            }),
            array.dropLeft(1),
            array.map((r) => ({
                ...r.prev,
                rank: r.rank,
            }))
        );
    };

interface TableRowIndividualKnown extends Omit<TableRowIndividual, "result"> {
    result: ResultWithCategoryTitle;
    clubTitle: string;
}
const tableRowIndividualOrdByRank = pipe(
    number.Ord,
    ord.contramap((row: TableRowIndividualKnown) => row.rank)
);
interface TableRowClub {
    id: string;
    rank: number;
    club: string;
    sum: number;
    individuals: NonEmptyArray<TableRowIndividualKnown>;
}
interface ClubProps extends LiveResultsProps {
    clubScoring: ClubScoring;
}
export const LiveResultsClubs = (p: ClubProps) => {
    const individualRows = useIndividualsRows(p);

    const tableCols = React.useMemo((): GridColumns<TableRowClub> => {
        const sumCol: GridEnrichedColDef<TableRowClub> = pipe(
            p.clubScoring.scoringMethod,
            (scoringMethod) => {
                switch (scoringMethod) {
                    case "timeSum":
                        return {
                            field: assertKey<TableRowClub>("sum"),
                            headerName: "Čas",
                            valueFormatter: (
                                params: GridValueFormatterParams<
                                    TableRowClub["sum"]
                                >
                            ) =>
                                pipe(
                                    params.value,
                                    storedTimeEntryCodec.decode,
                                    option.fromEither,
                                    option.fold(
                                        () => String(params.value),
                                        flow(
                                            timeEntryFromStored,
                                            timeEntryToString
                                        )
                                    )
                                ),
                        };

                    case "rankSum":
                        return {
                            field: assertKey<TableRowClub>("sum"),
                            headerName: "Soušet umístění",
                            type: "number",
                        };
                }

                assertUnreachable(scoringMethod);
            }
        );

        return [
            {
                field: assertKey<TableRowClub>("rank"),
                headerName: "Pořadí",
                maxWidth: 50,
            },
            {
                field: assertKey<TableRowClub>("club"),
                headerName: "Tým",
                flex: 0.5,
            },
            {
                field: "finished",
                headerName: "Členů v cíli",
                flex: 0.5,
                valueGetter: (
                    params: GridValueGetterParams<any, TableRowClub>
                ) => params.row.individuals.length,
            },
            sumCol,
        ];
    }, [p.clubScoring]);

    const rows = React.useMemo((): Array<TableRowClub> => {
        const clubScoringSum: (
            acc: number,
            row: TableRowIndividual
        ) => TableRowClub["sum"] = pipe(
            p.clubScoring.scoringMethod,
            (scoringMethod) => {
                switch (scoringMethod) {
                    case "timeSum":
                        return (acc, row) => acc + row.checkpointSum;

                    case "rankSum":
                        return (acc, row) => acc + row.rank;
                }

                assertUnreachable(scoringMethod);
            }
        );

        return pipe(
            individualRows.rows,
            array.filterMap((row) =>
                pipe(row.result, (result): Option<TableRowIndividualKnown> => {
                    return result.club_title == null
                        ? option.none
                        : option.some({
                              ...row,
                              result,
                              clubTitle: result.club_title,
                          });
                })
            ),
            nonEmptyArray.groupBy((individual) => individual.clubTitle),
            record.mapWithIndex((club, clubGroup) =>
                pipe(
                    clubGroup,
                    array.sort(tableRowIndividualOrdByRank),
                    array.takeLeft(p.clubScoring.scoringMembersCount),
                    (countingResults): TableRowClub => ({
                        id: nonEmptyArray.head(clubGroup).clubTitle,
                        club: nonEmptyArray.head(clubGroup).clubTitle,
                        individuals: clubGroup,
                        sum: pipe(
                            countingResults,
                            array.reduce(0, clubScoringSum)
                        ),
                        rank: 0, // TODO
                    })
                )
            ),
            record.toArray,
            array.map(tuple.snd),
            array.sort(clubRowOrd(p.clubScoring)),
            array.mapWithIndex((i, r) => ({
                ...r,
                rank: i + 1,
            }))
        );
    }, [individualRows.rows, p.clubScoring]);

    return (
        <DataTable
            loading={individualRows.loading}
            columns={tableCols}
            rows={rows}
        />
    );
};

interface WithOrdering {
    ordering: number;
}
const ordByOrderingAsc = <T extends WithOrdering>() =>
    pipe(
        number.Ord,
        contramap((p: T) => p.ordering)
    );

const useIndividualsRows = (p: LiveResultsProps) => {
    const segments = pipe(
        p.race.race_segment, // TODO make configurable in timing setup, segments does not have to be 1:1 with bibtag setup
        array.sort(ordByOrderingAsc<RaceSegment>()),
        array.map((raceSegmentItem) => raceSegmentItem.segment)
    );

    const resultsQuery = useResultsWithCategoryTitle([p.race.id], 10000);
    const resultByBib = React.useMemo(
        () =>
            pipe(
                resultsQuery.state,
                queryStateData,
                option.getOrElse(emptyArray()),
                array.filterMap((result) =>
                    pipe(
                        result.bib,
                        option.fromNullable,
                        option.map((bib) => ({ bib, result }))
                    )
                ),
                nonEmptyArray.groupBy(({ bib }) => bib)
                // tapLog("resultByBib")
            ),
        [resultsQuery.state]
    );
    useAlertOnQueryError(resultsQuery.state);

    const bibPassQuery = useBibPassAfterStart(p.race.id);
    useAlertOnQueryError(bibPassQuery.state);

    React.useEffect(() => {
        bibPassQuery.mutate();
    }, [resultByBib]);

    const checkpoints = pipe(
        p.race.fastest_expected_pace,
        option.fromNullable,
        option.getOrElse(() => paceMaxDefault(p.race.sport)),
        (pace) => {
            const initAcc: Checkpoint = {
                distance: 0 as Distance,
                timeLimit: 0 as StoredTimeEntry,
            };

            return pipe(
                segments,
                array.scanLeft(
                    initAcc,
                    (acc, segment): Checkpoint => ({
                        distance: (acc.distance + segment.distance) as Distance, // TODO calculus
                        timeLimit: (pace * segment.distance) as StoredTimeEntry,
                    })
                ),
                array.dropLeft(1) // cleanup after scanLeft
            );
        },
        nonEmptyArray.fromArray,
        someOrThrow
    );

    const rows = React.useMemo(() => {
        const gunshotTimeEntry = pipe(
            p.gunshot,
            (s) => s.split("T")[1],
            timeEntryFromTimeStringCodec.decode,
            rightOrThrow
        );

        return pipe(
            bibPassQuery.state,
            queryStateData,
            option.getOrElse(emptyArray()),
            nonEmptyArray.groupBy((pass) => pass.bib),
            record.toArray,
            array.filterMap(([bib, passList]) => {
                const resultOpt = pipe(
                    resultByBib,
                    record.lookup(bib),
                    option.map(flow(nonEmptyArray.head, ({ result }) => result))
                );
                // TODO what if bib without result?

                return pipe(
                    resultOpt,
                    option.map((result): TableRowIndividualWithoutRank => {
                        const startTimeEntry = pipe(
                            result.start_time,
                            option.fromNullable,
                            option.fold(
                                () => gunshotTimeEntry,
                                (startTime) => gunshotTimeEntry + startTime
                            ),
                            (startTime) => startTime as StoredTimeEntry
                        );

                        const checkpointTimes = pipe(
                            passList,
                            computeCheckpointTimes(startTimeEntry, {
                                checkpointList: checkpoints,
                                fluidLapsCount: p.race.kind === "duration",
                            })
                        );
                        return {
                            id: bib,
                            bib: bib,
                            result,
                            checkpointTimes,
                            checkpointSum: pipe(
                                checkpointTimes,
                                array.last,
                                option.getOrElse(() => 0 as StoredTimeEntry)
                            ),
                            laps: checkpointTimes.length,
                        };
                    })
                );
            }),
            array.filter(
                (row) =>
                    p.categoryFilter.length === 0 ||
                    (row.result.category_title != null &&
                        p.categoryFilter.includes(row.result.category_title))
            ),
            nonEmptyArray.fromArray,
            option.map(addRank(lapsRaceOrd)),
            option.getOrElse(emptyArray())
        );
    }, [bibPassQuery.state, resultByBib, p.categoryFilter]);

    return {
        rows,
        checkpointsCount: pipe(
            rows,
            array.foldMap(monoid.max(number.Bounded))(
                (row: TableRowIndividual) => row.checkpointTimes.length
            )
        ),
        loading: !isCompletedState(resultsQuery.state),
    };
};

const ordByFinishersCountDesc = (scoringMembersCount: number) =>
    pipe(
        number.Ord,
        contramap((r: TableRowClub) =>
            Math.min(r.individuals.length, scoringMembersCount)
        ),
        ord.reverse
    );

const ordByTimeAsc = pipe(
    number.Ord,
    contramap((r: TableRowClub) => r.sum)
);

const clubRowOrd = (clubScoring: ClubScoring) =>
    monoid.concatAll(ord.getMonoid<TableRowClub>())([
        ordByFinishersCountDesc(clubScoring.scoringMembersCount),
        ordByTimeAsc,
    ]);

const computeCheckpointTimes =
    (startTimeEntry: StoredTimeEntry, assignPassDeps: ComputedIgnoreDeps) =>
    (bibPassGroup: NonEmptyArray<BibPass>) =>
        pipe(
            bibPassGroup,
            nonEmptyArray.map(addRelativeTimeEntry(startTimeEntry)),
            computeIgnored(assignPassDeps),
            array.filter(isPassNotIgnored),
            array.map((pass) => pass.startRelativeTimeEntry)
        );

const isPassNotIgnored = (pass: BibPassWithComputedIgnore) =>
    !pass.ignored && !pass.computedIgnore;

interface ComputedIgnoreDeps {
    checkpointList: NonEmptyArray<Checkpoint>;
    fluidLapsCount: boolean;
}
const computeIgnored =
    (d: ComputedIgnoreDeps) =>
    (
        bibPassList: NonEmptyArray<BibPassWithGunshotRelativeTimeEntry>
    ): Array<BibPassWithComputedIgnore> => {
        const bibPassListSorted = pipe(
            bibPassList,
            nonEmptyArray.sort(ordByGunshotRelativeTimeEntry)
        );

        // TODO sort checkpoints as well?

        let checkPointIndex = 0;
        const result: Array<BibPassWithComputedIgnore> = [];
        let usedTimeSum = 0;

        for (
            let passIndex = 0;
            passIndex < bibPassListSorted.length;
            passIndex++
        ) {
            const pass = bibPassListSorted[passIndex];

            if (checkPointIndex >= d.checkpointList.length) {
                if (d.fluidLapsCount) {
                    checkPointIndex = 0;
                } else {
                    break;
                }
            }

            const checkpointAssigned = d.checkpointList[checkPointIndex];
            const segmentTime = pass.startRelativeTimeEntry - usedTimeSum;

            if (!pass.ignored && segmentTime >= checkpointAssigned.timeLimit) {
                result.push({
                    ...pass,
                    computedIgnore: false,
                });

                usedTimeSum = usedTimeSum + segmentTime;
                checkPointIndex++;
            } else {
                result.push({
                    ...pass,
                    computedIgnore: true,
                });
            }
        }

        return result;
    };

const addRelativeTimeEntry =
    (startTimeEntry: StoredTimeEntry) =>
    (pass: BibPass): BibPassWithGunshotRelativeTimeEntry => ({
        ...pass,
        startRelativeTimeEntry: pipe(
            pass.time,
            timeEntryFromTimeStringCodec.decode,
            rightOrThrow, // TODO? one wrong item kill whole result list
            (timeEntry) => timeEntry - startTimeEntry, // calculus
            storedTimeEntryCodec.decode,
            rightOrThrow // TODO? one wrong item kill whole result list
        ),
    });
